<script setup lang="ts">
/**
 * Left-rail conversation list, grouped by assistant.
 *
 * UX shape:
 *   - One group header per assistant ("Booking Matcher · 4 chats").
 *   - The currently-selected assistant's group is expanded; other
 *     groups collapse to just their header + count. Click the header
 *     to toggle.
 *   - "+ New" still creates a fresh chat under the current assistant.
 *
 * Why grouped (not filter-by-current): a single global "show only
 * the current assistant's chats" hides prior threads behind an
 * assistant switch, which breaks the operator's "where did I leave
 * off" workflow once 30+ chats live across 5 assistants. Grouping
 * keeps everything discoverable while collapsing the noise.
 *
 * Emits:
 *   - `select`: user clicked an existing conversation (→ rehydrate)
 *   - `new`: user clicked "New chat" (→ reset view)
 */
import { computed, onMounted, ref, watch } from "vue";
import {
  deleteConversation,
  listConversations,
  type Assistant,
  type ConversationSummary,
} from "../api/conversations";
import { assistantTint } from "../assistant-visuals";
import { useConfirm } from "../composables/useConfirm";

const { confirm } = useConfirm();

const props = defineProps<{
  devUser: string;
  activeId: number | null;
  /** bump this prop to force a reload (parent increments after sending) */
  refreshKey: number;
  /** Current assistant slug — group for this slug is expanded by default. */
  currentAssistantSlug: string;
  /** Display-name lookup so headers can show "Booking Matcher" instead
   *  of "booking-matcher". Empty = fall back to the slug. */
  assistants: Assistant[];
}>();

const emit = defineEmits<{
  (e: "select", id: number): void;
  (e: "new"): void;
}>();

const items = ref<ConversationSummary[]>([]);
const loading = ref(false);

/** Per-group collapsed state. Anything not in the map is treated as
 *  "expanded if it's the current assistant's group, collapsed otherwise"
 *  — so a fresh sidebar matches the user's selection without needing to
 *  pre-populate the map. Toggling sets an explicit entry so the choice
 *  sticks until the operator changes it. */
const collapsed = ref<Record<string, boolean>>({});

async function reload() {
  loading.value = true;
  try {
    items.value = await listConversations(props.devUser);
  } finally {
    loading.value = false;
  }
}

async function onDelete(id: number, title: string, ev: Event) {
  ev.stopPropagation();
  // The app's own dialog, not `window.confirm` — this and UploadedDocsPanel
  // were the last two OS dialogs in the product, and they landed on the one
  // surface users spend all day in.
  const ok = await confirm({
    title: "Delete this chat?",
    message: `“${title}” and its messages will be removed. This cannot be undone.`,
    confirmLabel: "Delete",
    danger: true,
  });
  if (!ok) return;
  if (await deleteConversation(id, props.devUser)) {
    items.value = items.value.filter((c) => c.id !== id);
    if (props.activeId === id) emit("new");
  }
}

onMounted(reload);
watch(() => props.refreshKey, reload);
watch(() => props.devUser, reload);

function shortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

interface GroupedConversations {
  slug: string;
  /** Display name from `assistants` if available; otherwise the slug
   *  itself (e.g. for legacy conversations whose assistant was renamed
   *  or deleted — better to show "deprecated-old-slug" than swallow). */
  label: string;
  isCurrent: boolean;
  conversations: ConversationSummary[];
}

const groupedConversations = computed<GroupedConversations[]>(() => {
  const labelBySlug = new Map(props.assistants.map((a) => [a.slug, a.name]));
  const bySlug = new Map<string, ConversationSummary[]>();
  for (const c of items.value) {
    const slug = c.assistant_slug || "(unknown)";
    const arr = bySlug.get(slug) ?? [];
    arr.push(c);
    bySlug.set(slug, arr);
  }
  // Two-tier order: the current assistant's group first, then the rest
  // by most recent activity. Inside each group, the backend already
  // returns conversations newest-first; we don't re-sort.
  const groups: GroupedConversations[] = [];
  const currentSlug = props.currentAssistantSlug;
  if (currentSlug && bySlug.has(currentSlug)) {
    groups.push({
      slug: currentSlug,
      label: labelBySlug.get(currentSlug) ?? currentSlug,
      isCurrent: true,
      conversations: bySlug.get(currentSlug)!,
    });
    bySlug.delete(currentSlug);
  }
  // Remaining groups, sorted by their most-recent conversation's
  // updated_at so "active recently" assistants float to the top.
  const remaining = Array.from(bySlug.entries()).map(([slug, convos]) => ({
    slug,
    label: labelBySlug.get(slug) ?? slug,
    isCurrent: false,
    conversations: convos,
    mostRecent: convos[0]?.updated_at ?? "",
  }));
  remaining.sort((a, b) => (a.mostRecent < b.mostRecent ? 1 : -1));
  for (const r of remaining) {
    groups.push({
      slug: r.slug,
      label: r.label,
      isCurrent: r.isCurrent,
      conversations: r.conversations,
    });
  }
  return groups;
});

function isCollapsed(g: GroupedConversations): boolean {
  const explicit = collapsed.value[g.slug];
  if (explicit !== undefined) return explicit;
  // Default: current assistant's group is open, others are collapsed.
  return !g.isCurrent;
}

function toggleGroup(slug: string) {
  const groups = groupedConversations.value;
  const g = groups.find((x) => x.slug === slug);
  if (!g) return;
  collapsed.value = { ...collapsed.value, [slug]: !isCollapsed(g) };
}

function chatsLabel(n: number): string {
  return n === 1 ? "1 chat" : `${n} chats`;
}
</script>

<template>
  <aside class="convos">
    <div class="convos__head">
      <span class="convos__title">Chats</span>
      <button class="convos__new" @click="emit('new')">
        <span aria-hidden="true">+</span> New
      </button>
    </div>
    <p v-if="loading && items.length === 0" class="convos__empty">Loading…</p>
    <div v-else-if="items.length === 0" class="convos__empty">
      <p class="convos__empty-title">No chats yet</p>
      <p class="convos__empty-hint">Your questions will show up here.</p>
    </div>
    <div v-else class="convos__groups">
      <section
        v-for="g in groupedConversations"
        :key="g.slug"
        class="convos__group"
        :class="{ 'convos__group--current': g.isCurrent }"
      >
        <button
          type="button"
          class="convos__group-header"
          :aria-expanded="!isCollapsed(g)"
          @click="toggleGroup(g.slug)"
        >
          <span class="convos__group-chevron" aria-hidden="true">
            {{ isCollapsed(g) ? "▸" : "▾" }}
          </span>
          <!-- The same colour this assistant wears on its Hub tile, so the
               rail can be scanned by hue rather than read. -->
          <span
            class="convos__group-dot"
            aria-hidden="true"
            :style="{ '--tint': assistantTint(g.slug) }"
          ></span>
          <span class="convos__group-label" :title="g.label">
            {{ g.label }}
          </span>
          <span class="convos__group-count">
            {{ chatsLabel(g.conversations.length) }}
          </span>
        </button>
        <ul v-if="!isCollapsed(g)" class="convos__list">
          <li
            v-for="c in g.conversations"
            :key="c.id"
            :class="[
              'convos__item',
              { 'convos__item--active': c.id === activeId },
            ]"
            @click="emit('select', c.id)"
          >
            <div class="convos__row">
              <span class="convos__item-title" :title="c.title">{{
                c.title
              }}</span>
              <button
                class="convos__del"
                :aria-label="`Delete conversation ${c.title}`"
                @click="onDelete(c.id, c.title, $event)"
              >
                ×
              </button>
            </div>
            <div class="convos__meta">
              <span>{{ shortDate(c.updated_at) }}</span>
            </div>
          </li>
        </ul>
      </section>
    </div>
  </aside>
</template>

<style scoped>
.convos {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  /* Sidebar: hold a comfortable fixed range. The flex parent
     gave it whatever the content demanded before, which meant
     long conversation titles ate the chat column. Cap to ~280px
     and let the title text truncate inside. */
  flex: 0 0 17rem;
  min-width: 0;
  padding: var(--space-3);
  /* A rounded glass panel, matching the transcript and composer it sits
     beside. It used to be a hard-edged grey slab with a right border — the
     one square-cornered opaque surface in an app built of glass cards. */
  border-radius: var(--radius-lg);
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  box-shadow: var(--shadow-md);
}
.convos__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.convos__title {
  font-weight: 500;
  color: var(--color-muted);
}
.convos__new {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid color-mix(in srgb, var(--color-accent) 22%, transparent);
  border-radius: var(--radius-pill);
  background: color-mix(in srgb, var(--color-accent) 9%, transparent);
  color: var(--color-accent);
  cursor: pointer;
  transition: background var(--dur-1) var(--ease-out);
}
.convos__new:hover {
  background: color-mix(in srgb, var(--color-accent) 16%, transparent);
}
.convos__empty {
  color: var(--color-muted);
  font-size: 0.85rem;
  padding: var(--space-3) var(--space-2);
}
.convos__empty-title {
  margin: 0 0 var(--space-1);
  color: var(--color-ink);
  font-weight: 600;
  font-size: 0.9rem;
}
.convos__empty-hint {
  margin: 0;
  line-height: 1.5;
}
.convos__groups {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  overflow-y: auto;
  max-height: 70vh;
}
.convos__group {
  display: flex;
  flex-direction: column;
}
.convos__group-header {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0.25rem 0.4rem;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font: inherit;
  border-radius: var(--radius-sm);
}
.convos__group-header:hover {
  background: color-mix(in srgb, var(--color-ink) 4%, transparent);
}
.convos__group-dot {
  flex: none;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: var(--tint);
  opacity: 0.75;
}
.convos__group-chevron {
  color: var(--color-muted);
  width: 0.9em;
  text-align: center;
  font-size: 0.8em;
  flex-shrink: 0;
}
.convos__group-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-muted);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 0;
  min-width: 0;
}
.convos__group--current .convos__group-label {
  color: var(--color-ink);
}
.convos__group-count {
  font-size: 0.7rem;
  color: var(--color-muted);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}
.convos__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-top: var(--space-1);
}
.convos__item {
  padding: var(--space-2);
  border-radius: var(--radius-md);
  cursor: pointer;
  border: 1px solid transparent;
}
.convos__item:hover {
  background: color-mix(in srgb, var(--color-ink) 4%, transparent);
}
/* Was a literal `rgb(13 71 161)` — a hardcoded blue that survived every
   rebrand, so a crimson or green tenant still got a blue active row. */
.convos__item--active {
  background: color-mix(in srgb, var(--color-accent) 10%, transparent);
  border-color: color-mix(in srgb, var(--color-accent) 30%, transparent);
}
.convos__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-1);
}
.convos__item-title {
  font-size: 0.9rem;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  flex: 1;
}
/* Revealed on hover/focus rather than sitting on every row — a column of
   × glyphs reads as clutter, and as a risk. Always reachable by keyboard. */
.convos__del {
  border: none;
  background: transparent;
  font-size: 1rem;
  line-height: 1;
  color: var(--color-muted);
  cursor: pointer;
  padding: 0 0.2rem;
  opacity: 0;
  transition:
    opacity var(--dur-1) var(--ease-out),
    color var(--dur-1) var(--ease-out);
}
.convos__item:hover .convos__del,
.convos__del:focus-visible {
  opacity: 1;
}
.convos__del:hover {
  color: var(--color-error);
}
@media (hover: none) {
  /* No hover to reveal it — keep it visible on touch. */
  .convos__del {
    opacity: 1;
  }
}
.convos__meta {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.15rem;
  color: var(--color-muted);
  font-size: 0.75rem;
}
</style>
